/// <amd-module name="Repos/VersionDialogs/Components/Create/CreateBranchDialog" />
import * as React from "react";
import { GitRepository, GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { getInvalidBranchNameMessage, refToBranchName } from "Repos/Common/Util/Ref";
import * as Resources from "Repos/VersionDialogs/Resources";
import { IGitRefService } from "Repos/VersionDropdown/GitRefService";
import { IOptionsViewService } from "Repos/Views/Settings/Options/Services/OptionsViewService";
import { ObservableValue } from "VSS/Core/Observable";
import { IMessageDialogProps, MessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IRegionComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { DialogResult, getService as getVersionDialogsService, IVersionDialogWithLinkingContext, LinkingError } from "../../VersionDialogsService";
import { BranchCreateListener, ICreateBranchDialogProps, ILinkedWorkItemsCreateBranchProps } from "./CreateBranchDialog.Props";
import { CreateDialog } from "./CreateDialog";
import { ICreateDialogProps } from "./CreateDialog.Props";

export class CreateBranchDialog extends VssComponent<ICreateBranchDialogProps> {
    private createListener: BranchCreateListener | undefined;
    private createResult = new ObservableValue<DialogResult>({});
    private linkingError = new ObservableValue<LinkingError>({});

    public render(): JSX.Element {
        return (
            <Observer linkingError={this.linkingError}>
                {(props: { linkingError: LinkingError }) => {
                    const { branchName, referenceVersion, onComplete, onDismiss, repository, allowChangingRepository } = this.props;

                    if (props.linkingError.error && props.linkingError.branch) {
                        // Branch created but linking failed. In this case, we don't display form.
                        // Instead, we just show warning message and close button
                        const { branch, error } = props.linkingError;
                        const messageDialogProps: IMessageDialogProps = {
                            lightDismiss: true,
                            message: (
                                <>
                                    <FormatComponent elementType={"p"} format={Resources.WorkItemLinkingFailedMessage}>
                                        <strong>{refToBranchName(branch.name)}</strong>
                                    </FormatComponent>
                                    <MessageCard severity={MessageCardSeverity.Warning}>{error.message}</MessageCard>
                                    <p>{Resources.WorkItemLinkingSuggestionMessage}</p>
                                </>
                            ),
                            okButtonProps: {
                                text: Resources.CloseButtonText,
                                primary: true
                            },
                            onClose: (result: boolean) => {
                                if (result) {
                                    onComplete(branch);
                                }
                            },
                            showCancel: false,
                            title: Resources.CreateBranchDialogTitle
                        };

                        return <MessageDialog {...messageDialogProps} />;
                    } else {
                        const refService = this.context.pageContext.getService<IGitRefService>("IGitRefService");

                        // Show the form if there is no linking issue
                        const createDialogProps: ICreateDialogProps = {
                            createResult: this.createResult,
                            dialogTitle: Resources.CreateBranchDialogTitle,
                            getRefByName: (name, repository) => refService.getBranchByName(repository!.id, repository!.project.id, name),
                            getNameSuggestion: isFeatureFlagEnabled(this.context.pageContext, "SourceControl.SuggestBranchName", false) ? this.getNameSuggestion : undefined,
                            initialName: branchName,
                            initialReferenceVersion: referenceVersion,
                            linkingRegion: () => (
                                <WrappedComponent<IRegionComponentProperties>
                                    dependencies={["ms.vss-code-web.create-branch-dialog-extensions"]}
                                    wrappedType="ms.vss-web.region"
                                    wrappedProps={{
                                        componentRegion: "createBranchDialogExtensions",
                                        childComponentProperties: {
                                            createBranchContext: {
                                                addBranchCreateListener: listener => (this.createListener = listener)
                                            },
                                            initialWorkItemIds: this.props.initialWorkItemIds
                                        } as ILinkedWorkItemsCreateBranchProps
                                    }}
                                />
                            ),
                            nameExistsMessage: Resources.BranchAlreadyExistsMessage,
                            onCreateClick: this.onCreateClick,
                            onCreateComplete: onComplete,
                            onDismiss,
                            onValidateName: getInvalidBranchNameMessage,
                            repository,
                            allowChangingRepository
                        };

                        return <CreateDialog {...createDialogProps} />;
                    }
                }}
            </Observer>
        );
    }

    private onCreateClick = (branchName: string, description: string, baseVersion: GitVersionDescriptor, repository: GitRepository): void => {
        // This work item linking promise gets evaluated after making sure that create branch is succeeded
        const workItemlinkingPromise = this.createListener ? this.createListener(repository!, branchName) || undefined : undefined;
        const dialogContext: IVersionDialogWithLinkingContext = {
            dialogResult: this.createResult,
            linkingError: this.linkingError,
            repository: repository
        };

        getVersionDialogsService(this.context.pageContext).createBranch(dialogContext, branchName, baseVersion, workItemlinkingPromise);
    };

    private getNameSuggestion = (repository: GitRepository): Promise<string | undefined> => {
        const optionsSvc = this.context.pageContext.getService<IOptionsViewService>("OptionsViewService");
        return optionsSvc.getRepositoryOptions(repository.project.id, repository.id, false).then(options => {
            return this.parseSuggestion(options.find(option => option.key === "Suggestion")?.textValue ?? "");
        });
    };

    private getUserName = (): string => {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        let userName = "";
        if (vssPageData) {
            userName = vssPageData.user.uniqueName;
        }
        if (userName !== "") {
            userName = userName.split("@")[0];
        }
        return userName;
    };

    private parseSuggestion = (suggestion: string): string => {
        var formatedString = suggestion;
        if (formatedString) {
            formatedString = formatedString.replace("{userAlias}", this.getUserName());
            formatedString = formatedString.replace("{workitemId}", this.props.initialWorkItemIds?.length === 1 ? this.props.initialWorkItemIds[0].toString() : "");
        }
        return formatedString;
    };
}

Components.add("ms.vss-code-web.create-branch-dialog", CreateBranchDialog);
